import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Undo2, Redo2, Trash2, Save, Square, Grid3x3 } from "lucide-react";

/**
 * Essential manual-blur editor:
 *  - draw rectangles by click+drag
 *  - move rectangles (drag inside)
 *  - resize rectangles (drag from any edge/corner ~ 14px handle)
 *  - delete rectangle (double-click)
 *  - undo / redo history
 *  - mode blur | pixelate
 *  - saves as a new blurred version via /admin/photos/blur-regions and swaps it in.
 */
export default function ManualBlurEditor({ open, onOpenChange, sourcePath, target, onSaved }) {
  // target: { report_id?, photo_index?, additional_photo_id?, urgent_alert_id?, extension_id? }
  const { t } = useI18n();
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [regions, setRegions] = useState([]); // {id, x, y, w, h, mode}
  const [historyPast, setHistoryPast] = useState([]);
  const [historyFuture, setHistoryFuture] = useState([]);
  const [mode, setMode] = useState("blur");
  const [drawing, setDrawing] = useState(null); // {id, startX, startY}
  const [dragging, setDragging] = useState(null); // {id, dx, dy}
  const [resizing, setResizing] = useState(null); // {id, handle, sx, sy, orig}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setRegions([]); setHistoryPast([]); setHistoryFuture([]);
    }
  }, [open]);

  const pushHistory = (prev) => {
    setHistoryPast((h) => [...h.slice(-49), prev]);
    setHistoryFuture([]);
  };

  const undo = () => {
    setHistoryPast((past) => {
      if (past.length === 0) return past;
      const last = past[past.length - 1];
      setHistoryFuture((f) => [regions, ...f]);
      setRegions(last);
      return past.slice(0, -1);
    });
  };
  const redo = () => {
    setHistoryFuture((fut) => {
      if (fut.length === 0) return fut;
      const [next, ...rest] = fut;
      setHistoryPast((p) => [...p, regions]);
      setRegions(next);
      return rest;
    });
  };
  const clearAll = () => { if (regions.length === 0) return; pushHistory(regions); setRegions([]); };

  const onImageLoad = () => {
    if (!imgRef.current) return;
    setImgSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    setDisplaySize({ w: imgRef.current.clientWidth, h: imgRef.current.clientHeight });
  };

  const relPos = (evt) => {
    const rect = containerRef.current.getBoundingClientRect();
    const cx = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const cy = evt.touches ? evt.touches[0].clientY : evt.clientY;
    return { x: Math.max(0, Math.min(displaySize.w, cx - rect.left)), y: Math.max(0, Math.min(displaySize.h, cy - rect.top)) };
  };

  const onCanvasMouseDown = (evt) => {
    if (evt.target !== evt.currentTarget && !evt.target.dataset?.canvasArea) return;
    const p = relPos(evt);
    const id = "r_" + Math.random().toString(36).slice(2, 9);
    setDrawing({ id, startX: p.x, startY: p.y });
    pushHistory(regions);
    setRegions((rs) => [...rs, { id, x: p.x, y: p.y, w: 1, h: 1, mode }]);
  };
  const onCanvasMouseMove = (evt) => {
    if (drawing) {
      const p = relPos(evt);
      setRegions((rs) => rs.map((r) => r.id === drawing.id ? {
        ...r,
        x: Math.min(drawing.startX, p.x),
        y: Math.min(drawing.startY, p.y),
        w: Math.max(4, Math.abs(p.x - drawing.startX)),
        h: Math.max(4, Math.abs(p.y - drawing.startY)),
      } : r));
    } else if (dragging) {
      const p = relPos(evt);
      setRegions((rs) => rs.map((r) => r.id === dragging.id ? {
        ...r,
        x: Math.max(0, Math.min(displaySize.w - r.w, p.x - dragging.dx)),
        y: Math.max(0, Math.min(displaySize.h - r.h, p.y - dragging.dy)),
      } : r));
    } else if (resizing) {
      const p = relPos(evt);
      const dx = p.x - resizing.sx; const dy = p.y - resizing.sy;
      setRegions((rs) => rs.map((r) => {
        if (r.id !== resizing.id) return r;
        const o = resizing.orig;
        let nx = o.x, ny = o.y, nw = o.w, nh = o.h;
        if (resizing.handle.includes("e")) nw = Math.max(6, o.w + dx);
        if (resizing.handle.includes("s")) nh = Math.max(6, o.h + dy);
        if (resizing.handle.includes("w")) { nx = o.x + dx; nw = Math.max(6, o.w - dx); }
        if (resizing.handle.includes("n")) { ny = o.y + dy; nh = Math.max(6, o.h - dy); }
        // clamp to bounds
        nx = Math.max(0, nx); ny = Math.max(0, ny);
        if (nx + nw > displaySize.w) nw = displaySize.w - nx;
        if (ny + nh > displaySize.h) nh = displaySize.h - ny;
        return { ...r, x: nx, y: ny, w: nw, h: nh };
      }));
    }
  };
  const endInteraction = () => { setDrawing(null); setDragging(null); setResizing(null); };

  const onRegionMouseDown = (evt, r) => {
    evt.stopPropagation();
    const p = relPos(evt);
    setDragging({ id: r.id, dx: p.x - r.x, dy: p.y - r.y });
  };
  const onHandleMouseDown = (evt, r, handle) => {
    evt.stopPropagation();
    const p = relPos(evt);
    setResizing({ id: r.id, handle, sx: p.x, sy: p.y, orig: { x: r.x, y: r.y, w: r.w, h: r.h } });
  };
  const onRegionDoubleClick = (evt, r) => {
    evt.stopPropagation();
    pushHistory(regions);
    setRegions((rs) => rs.filter((x) => x.id !== r.id));
  };

  const save = async () => {
    if (regions.length === 0) { toast.error(t("manual_blur_no_regions")); return; }
    setBusy(true);
    try {
      const payload = {
        source_path: sourcePath,
        regions: regions.map((r) => ({ x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h), mode: r.mode || mode })),
        display_w: Math.round(displaySize.w),
        display_h: Math.round(displaySize.h),
        mode_default: mode,
        ...(target || {}),
      };
      const { data } = await api.post("/admin/photos/blur-regions", payload);
      toast.success(t("manual_blur_saved"));
      onOpenChange(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-blur-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("manual_blur_title")}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-2">{t("manual_blur_desc")} · <span className="italic">{t("manual_blur_hint")}</span></div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="flex bg-slate-100 rounded-full p-0.5">
            <button data-testid="mb-mode-blur" onClick={() => setMode("blur")} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${mode==="blur" ? "bg-white shadow" : "text-slate-600"}`}><Square size={10} /> {t("manual_blur_mode_blur")}</button>
            <button data-testid="mb-mode-pixel" onClick={() => setMode("pixelate")} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${mode==="pixelate" ? "bg-white shadow" : "text-slate-600"}`}><Grid3x3 size={10} /> {t("manual_blur_mode_pixel")}</button>
          </div>
          <Button data-testid="mb-undo" variant="outline" size="sm" onClick={undo} disabled={historyPast.length === 0} className="rounded-full text-xs h-8 hover:bg-slate-100"><Undo2 size={12} className="mr-1" />{t("manual_blur_undo")}</Button>
          <Button data-testid="mb-redo" variant="outline" size="sm" onClick={redo} disabled={historyFuture.length === 0} className="rounded-full text-xs h-8 hover:bg-slate-100"><Redo2 size={12} className="mr-1" />{t("manual_blur_redo")}</Button>
          <Button data-testid="mb-clear" variant="outline" size="sm" onClick={clearAll} disabled={regions.length === 0} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800"><Trash2 size={12} className="mr-1" />{t("manual_blur_clear")}</Button>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 ml-auto">Regioni: <b className="text-slate-900" data-testid="mb-count">{regions.length}</b></div>
        </div>
        <div
          ref={containerRef}
          onMouseDown={onCanvasMouseDown}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={endInteraction}
          onMouseLeave={endInteraction}
          onTouchStart={onCanvasMouseDown}
          onTouchMove={onCanvasMouseMove}
          onTouchEnd={endInteraction}
          className="relative select-none bg-slate-950 rounded-xl overflow-hidden touch-none"
          style={{ minHeight: 120 }}
          data-testid="mb-canvas"
        >
          <img
            ref={imgRef}
            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${sourcePath}`}
            alt="original"
            onLoad={onImageLoad}
            data-canvas-area="1"
            draggable={false}
            className="block w-full h-auto max-h-[65vh] object-contain pointer-events-none"
          />
          {regions.map((r) => (
            <div
              key={r.id}
              data-testid={`mb-region-${r.id}`}
              onMouseDown={(e) => onRegionMouseDown(e, r)}
              onTouchStart={(e) => onRegionMouseDown(e, r)}
              onDoubleClick={(e) => onRegionDoubleClick(e, r)}
              className="absolute border-2 border-emerald-400/90 shadow-[0_0_0_2px_rgba(0,0,0,0.4)] cursor-move"
              style={{
                left: r.x, top: r.y, width: r.w, height: r.h,
                background: r.mode === "pixelate"
                  ? "repeating-linear-gradient(45deg,rgba(16,185,129,0.35),rgba(16,185,129,0.35) 4px,rgba(0,0,0,0.15) 4px,rgba(0,0,0,0.15) 8px)"
                  : "rgba(16,185,129,0.28)",
                backdropFilter: r.mode !== "pixelate" ? "blur(4px)" : undefined,
                WebkitBackdropFilter: r.mode !== "pixelate" ? "blur(4px)" : undefined,
              }}
            >
              {/* handles */}
              {["nw","n","ne","w","e","sw","s","se"].map((h) => {
                const style = {};
                if (h.includes("n")) style.top = -6; else if (h.includes("s")) style.bottom = -6;
                else style.top = "50%", style.marginTop = -6;
                if (h.includes("w")) style.left = -6; else if (h.includes("e")) style.right = -6;
                else style.left = "50%", style.marginLeft = -6;
                const cursor = { n: "ns", s: "ns", e: "ew", w: "ew", ne: "nesw", sw: "nesw", nw: "nwse", se: "nwse" }[h];
                return (
                  <div key={h} data-testid={`mb-handle-${r.id}-${h}`}
                       onMouseDown={(e) => onHandleMouseDown(e, r, h)}
                       onTouchStart={(e) => onHandleMouseDown(e, r, h)}
                       className="absolute w-3 h-3 bg-white border border-emerald-600 rounded-sm"
                       style={{ ...style, cursor: `${cursor}-resize` }} />
                );
              })}
            </div>
          ))}
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid="mb-save" onClick={save} disabled={busy || regions.length === 0} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Save size={14} className="mr-1" /> {t("manual_blur_save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
